import { useRef, useState, useCallback } from 'react'
import { dataManagerApi } from '../../../services/dataManagerApi'

interface ParsedFile {
  headers: string[]
  preview_rows: Record<string, unknown>[]
  row_count: number
  filename: string
}

interface Step2Props {
  companyId: number
  onParsed: (result: ParsedFile, file: File) => void
  onBack: () => void
}

const MAX_SIZE = 5 * 1024 * 1024 // 5 MB
const ACCEPTED = '.csv,.xlsx'

function Step2_UploadFile({ companyId, onParsed, onBack }: Step2Props) {
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedFile | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateFile = (f: File): string | null => {
    const ext = f.name.toLowerCase().split('.').pop()
    if (ext !== 'csv' && ext !== 'xlsx') return 'Only .csv and .xlsx files are accepted.'
    if (f.size > MAX_SIZE) return 'File exceeds 5 MB limit.'
    return null
  }

  const handleFile = useCallback(async (f: File) => {
    const err = validateFile(f)
    if (err) { setError(err); return }

    setError(null)
    setLoading(true)
    setParsed(null)
    setFile(f)

    try {
      const result = await dataManagerApi.parseFile(companyId, f)
      const parsedResult: ParsedFile = {
        headers: result.headers,
        preview_rows: result.preview_rows,
        row_count: result.row_count,
        filename: f.name,
      }
      setParsed(parsedResult)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to parse file'
      setError(msg)
      setFile(null)
    } finally {
      setLoading(false)
    }
  }, [companyId])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) handleFile(f)
  }, [handleFile])

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const onDragLeave = useCallback(() => setDragOver(false), [])

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleFile(f)
    // Reset so the same file can be re-selected
    e.target.value = ''
  }, [handleFile])

  const previewHeaders = parsed?.headers ?? []
  const previewRows = parsed?.preview_rows?.slice(0, 5) ?? []

  return (
    <div>
      <div className="iw-step-title">Upload File</div>
      <div className="iw-step-subtitle">Upload a CSV or Excel file (max 5 MB).</div>

      {error && (
        <div className="iw-error">
          {error}
          <button className="iw-error-dismiss" onClick={() => setError(null)}>x</button>
        </div>
      )}

      <div
        className={`iw-upload-zone${dragOver ? ' iw-upload-zone--dragover' : ''}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
      >
        <div className="iw-upload-icon">&#128462;</div>
        <div className="iw-upload-text">
          {loading ? 'Parsing file...' : 'Drag & drop your file here'}
        </div>
        <div className="iw-upload-hint">or click to browse</div>
        <button
          className="iw-upload-browse"
          type="button"
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click() }}
        >
          Browse Files
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          style={{ display: 'none' }}
          onChange={onInputChange}
        />
      </div>

      {loading && (
        <div className="iw-loading">
          <span className="iw-spinner" />
          Parsing file...
        </div>
      )}

      {parsed && (
        <div className="iw-preview-section">
          <div className="iw-preview-label">
            Preview: {parsed.filename} ({parsed.row_count} rows)
          </div>
          <div className="iw-preview-table-wrap">
            <table className="iw-preview-table">
              <thead>
                <tr>
                  {previewHeaders.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {previewHeaders.map((h) => (
                      <td key={h}>{row[h] != null ? String(row[h]) : ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="iw-btn-row">
        <button className="iw-btn iw-btn--secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="iw-btn iw-btn--primary"
          disabled={!parsed}
          onClick={() => parsed && file && onParsed(parsed, file)}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default Step2_UploadFile
export type { ParsedFile }
